
// Vue
import { Component, Vue, Prop, Mixins, Watch } from "vue-property-decorator";
import { namespace, Getter, Mutation, Action, State } from "vuex-class";


import { AudioDevice, AudioOptions, AudioTypes, DeviceSpec } from "@/types/audio/audio";
import MicMixin from '@/mixins/audio/MicMixin';
import { EventDetails } from '@/store/site-monitor/types';

const SiteMonitor = namespace("siteMonitor");

@Component({})
export default class Transmit extends Mixins(MicMixin) {
    // -- Props -- //

    @Prop({ default: null, type: Object }) audioTransmit: AudioDevice;
    @Prop({ default: "", type: String }) address: string;

	@SiteMonitor.State(state => state.EventDetails) eventDetails: EventDetails;

    // -- Properties -- //

    public active: boolean = false;
    private receiveActive: boolean = false;

    // -- Life Cycle Hooks -- //
    public async mounted(): Promise<void> {
        await this.setMic();
    }

    // -- Computed Properties -- //
    
    /**
     * @summary computed property which checks the state of the pref 'HoldToTransmit'
     * @returns { boolean }
     */
    private get isHoldToTansmitEnabled(): boolean {
        return (this.eventDetails && this.eventDetails.holdToTansmit) ?? false;
    }

    /**
     * @summary computed property which will return the varient of the transmit button
     * depending on the status of the mic, if it has permission, has ssl, enabled etc.
     * @returns { string } 'warning', 'primary' or 'secondary'
     */
    private get variant(): string {
        if (!this.deviceProtocol) {
            return "warning";
        }
        return this.active ? "primary" : "secondary";
    }

    // -- Watchers -- //

    @Watch("active")
    private async setMicActiveState(status: boolean) {
        await this.toggleMic(status);
    }

    @Watch("address")
    private async addressChange(newAddress: string) {
        this.active = false;
        await this.setMic();
    }

    // -- Methods -- //

    /**
     * @summary method which will toggle the active state of the button by the down event.
     */
    private transmitMouseDownEvent() {
        if (!this.isHoldToTansmitEnabled) return;
        this.active = true;
    }

    /**
     * @summary method which will toggle the active state of the button by the up event.
     */
    private transmitMouseUpEvent() {
        if (!this.isHoldToTansmitEnabled) return;
        this.active = false;
    }

    /**
     * @summary method which will toggle the active state of the button by the leave event.
     */
    private transmitMouseLeaveEvent() {
        if (!this.isHoldToTansmitEnabled) return;
        this.active = false;
    }

    /**
     * @summary method which will toggle the state of the active for transmit
     */
    private toggleActiveState() {
        if (this.isHoldToTansmitEnabled) return;
        this.active = !this.active;
    }

    /**
     * @summary method which will setup the mic instance
     */
    private async setMic() {
        if (!this.address) return;

        await this.setupMic({
            url: this.address,
            playingState: this.active
        });
    }

    /**
     * @summary method which will request the permission from the user if they didn't give the page mic permission
     */
    private async requestPermission(): Promise<void> {
        await this.requestDeviceAuth();
    }
}
